import { pick } from 'lodash';
import { OFFER_SPECIFIC_FIELDS } from './constants';
import { getCommissionFields } from './getCommissionFields';
import type { CoverSheetDocument, CoverSheetInteraction } from './types';

/**
 * Dispatches on `type` rather than probing for a `property` or `listing` key:
 * callers pass a normalized interaction that always carries `property`, which
 * would otherwise make a client group or referral its own listing.
 */
function getListing(interaction: CoverSheetInteraction) {
  switch (interaction.type) {
    case 'listing':
      return interaction;
    case 'offer':
    case 'lease':
      return interaction.listing ?? null;
    default:
      return null;
  }
}

function getTotalOffersReceived(interaction: CoverSheetInteraction) {
  if (interaction.type === 'listing') {
    return interaction.totalOffersReceived ?? null;
  }
  return interaction.listing?.totalOffersReceived ?? null;
}

/**
 * Assembles the cover-sheet document the form renders from.
 *
 * The spread order in the merged branch is load-bearing: the listing overwrites
 * the interaction's own `id`, `status`, `owner` and `notes`, which the
 * offer-specific pick and the explicit ids then restore.
 */
export function buildCoverSheetDocument(
  interaction: CoverSheetInteraction,
  identityIds: {
    ownerUserIdentityId?: string | null;
    teamIdentityId?: string | null;
  } = {},
): CoverSheetDocument {
  const listing = getListing(interaction);

  const shared = {
    id: interaction.id,
    interactionId: interaction.id,
    totalOffersReceived: getTotalOffersReceived(interaction),
    ownerUserIdentityId: identityIds.ownerUserIdentityId ?? null,
    teamIdentityId: identityIds.teamIdentityId ?? null,
  };

  // A lease's acceptance date always lives on its offer; personal deal lives
  // on the side the representation edits
  const offerOwnedFields =
    interaction.type === 'lease'
      ? {
          acceptanceDate: interaction.offer?.acceptanceDate ?? null,
          ...(interaction.representation === 'tenant' && {
            isPersonalDeal: interaction.offer?.isPersonalDeal ?? null,
          }),
        }
      : {};

  if (!listing) {
    return {
      ...interaction,
      ...shared,
      minimumPrice: interaction.priceRangeMin ?? null,
      maximumPrice: interaction.priceRangeMax ?? null,
      ...offerOwnedFields,
      ...getCommissionFields(interaction),
    };
  }

  return {
    ...interaction,
    ...listing,
    ...pick(interaction, OFFER_SPECIFIC_FIELDS),
    ...offerOwnedFields,
    ...shared,
    minimumPrice: null,
    maximumPrice: null,
    ...getCommissionFields(interaction),
  };
}
